import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Shield } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { HomeSEO } from '../components/SEOHead';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { SkipToMain } from '../components/Accessibility';

const homePath = () => { try { return /nurse|clinician|\brn\b|lvn|lpn/i.test(JSON.parse(localStorage.getItem('ivory_user') || '{}').role || '') ? '/dashboard' : '/dashboard/quick-links'; } catch { return '/dashboard/quick-links'; } };
import ForcePasswordChangeModal from '../components/ForcePasswordChangeModal';

const LoginPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [pending2FAUserId, setPending2FAUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  // Force password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [tempLoginData, setTempLoginData] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSuccessfulLogin = (response) => {
    // Store user with HIPAA info
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify({
      ...response.user,
      hipaa: response.hipaa
    }));
    localStorage.setItem('ivory_user', JSON.stringify({
      ...response.user,
      token: response.token
    }));
    
    // Check for password expiry warning
    if (response.hipaa?.passwordExpiresInDays <= 14 && !response.hipaa?.passwordExpired) {
      toast({
        title: "Password Expiring Soon",
        description: `Your password will expire in ${response.hipaa.passwordExpiresInDays} days.`,
      });
    }
    
    toast({
      title: "Login Successful!",
      description: "Welcome back to Ivory Health.",
    });
    navigate(homePath());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { authAPI } = await import('../api');
      const response = await authAPI.login(formData.email, formData.password);
      
      // Check if 2FA is required
      if (response.requires2FA) {
        setShow2FA(true);
        setPending2FAUserId(response.userId);
        toast({
          title: "2FA Required",
          description: response.message || "Check your email for verification code",
        });
        setLoading(false);
        return;
      }
      
      // Check if password change is required
      if (response.mustChangePassword) {
        setTempLoginData({
          token: response.token,
          email: response.user.email,
          response: response
        });
        setShowPasswordChange(true);
        toast({
          title: "Password Change Required",
          description: "Please create a new password to continue.",
        });
        setLoading(false);
        return;
      }
      
      handleSuccessfulLogin(response);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Invalid email or password";
      toast({
        title: "Login Failed",
        description: errorMessage,
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    if (!twoFACode || twoFACode.length !== 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter a valid 6-digit code",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: pending2FAUserId, code: twoFACode })
      });
      
      const data = await response.json();
      
      if (data.success) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify({
          ...data.user,
          hipaa: data.hipaa
        }));
        localStorage.setItem('ivory_user', JSON.stringify({
          ...data.user,
          token: data.token
        }));
        
        toast({
          title: "Login Successful!",
          description: "Two-factor authentication verified.",
        });
        navigate(homePath());
      } else {
        throw new Error(data.detail || 'Invalid code');
      }
    } catch (error) {
      toast({
        title: "Verification Failed",
        description: error.message || "Invalid 2FA code",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  return (
    <>
      <HomeSEO />
      <SkipToMain />
      <div className="min-h-screen bg-gradient-to-br from-[#f59e0b] to-[#ea580c] flex items-center justify-center p-4">
        {/* Language Switcher - Top Right */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <LanguageSwitcher />
        </div>
        
        <div className="max-w-sm w-full" id="main-content">
          <div className="bg-white rounded-2xl shadow-2xl p-6" role="main">
            {/* Logo - Exact Gold Logo with Rotation */}
            <div className="text-center mb-4">
              <Link to="/" className="inline-block" aria-label="Ivory Medical Home - Home">
                <img 
                  src="/images/ivory-logo-gold.png" 
                  alt="Ivory Medical Home" 
                  className="h-40 w-40 mx-auto mb-2 object-contain ivory-logo-spin"
                />
              </Link>
            </div>

            {/* HIPAA Notice - Compact */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-4" role="alert" aria-live="polite">
              <div className="flex items-center gap-2">
                <Shield className="w-3 h-3 text-blue-600 flex-shrink-0" aria-hidden="true" />
                <p className="text-[10px] text-blue-700">
                  Protected Health Information (PHI). Unauthorized access prohibited.
                </p>
              </div>
            </div>

            {show2FA ? (
              <>
                <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">Two-Factor Authentication</h2>
                <p className="text-sm text-gray-600 text-center mb-6">
                  Enter the 6-digit code sent to your email
                </p>
              
              <form onSubmit={handle2FASubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent text-center text-2xl tracking-widest"
                    placeholder="000000"
                    maxLength={6}
                    data-testid="2fa-input"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading || twoFACode.length !== 6}
                  className="w-full py-3 px-4 bg-[#ea580c] text-white font-semibold rounded-lg hover:bg-[#c94b0a] transition-colors disabled:opacity-50"
                  data-testid="verify-2fa-btn"
                >
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    setShow2FA(false);
                    setTwoFACode('');
                    setPending2FAUserId(null);
                  }}
                  className="w-full text-sm text-gray-600 hover:text-gray-800"
                >
                  ← Back to login
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-lg font-bold text-gray-800 mb-3 text-center">Sign in to your account</h2>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent text-sm"
                    placeholder="you@example.com"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-xs font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent text-sm"
                      placeholder="••••••••"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      className="h-3 w-3 text-[#ea580c] focus:ring-[#ea580c] border-gray-300 rounded"
                    />
                    <label htmlFor="remember-me" className="ml-1.5 block text-xs text-gray-700">
                      Remember me
                    </label>
                  </div>
                  <a href="#" className="text-xs text-[#ea580c] hover:text-[#f59e0b]">
                    Forgot password?
                  </a>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-4 py-2 bg-[#ff6634] text-white font-semibold rounded-lg hover:bg-[#e55a2d] transition-all disabled:opacity-50 text-sm"
                  data-testid="login-btn"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>

              <div className="mt-3 text-center">
                <p className="text-xs text-gray-600">
                  Don't have an account?{' '}
                  <Link to="/contact" className="text-[#ea580c] font-semibold hover:text-[#f59e0b]">
                    Contact Sales
                  </Link>
                </p>
              </div>

              {/* Demo credentials */}
              <div className="mt-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-[10px] text-gray-600 text-center">
                  <strong>Demo:</strong> test@test.com / testpassword
                </p>
              </div>
            </>
          )}
          </div>
        </div>
      </div>

      {/* Force Password Change Modal */}
      {showPasswordChange && tempLoginData && (
        <ForcePasswordChangeModal
          userEmail={tempLoginData.email}
          tempToken={tempLoginData.token}
          onPasswordChanged={() => {
            setShowPasswordChange(false);
            setTempLoginData(null);
            setFormData({ email: '', password: '' });
            toast({
              title: "Password Changed Successfully",
              description: "Please log in with your new password.",
            });
          }}
          onCancel={() => {
            setShowPasswordChange(false);
            setTempLoginData(null);
            setFormData({ email: '', password: '' });
          }}
        />
      )}
    </>
  );
};

export default LoginPage;
